import React from 'react';
import * as yup from 'yup';
import {QuickSettingsForm, FormFieldConfig} from '@/components/forms';
import i18n from 'i18n';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {RestrictedFieldInput} from '@/components/utils/DataNotAvailable';

interface AgentFormValues {
    firstName: string;
    lastName: string;
    externalId: string;
    user?: {
        id?: number;
        username?: string;
        email?: string;
    };
}

interface AgentSettingsFormProps {
    agentId?: number;
    onSave?: () => void;
}

const AgentSettingsForm: React.FC<AgentSettingsFormProps> = React.memo((
    {
        agentId = 0,
        onSave = () => {},
    },
) => {
    const {userData} = useOptimizedConfig();
    const restrictedFieldKeys: string[] = userData?.restrictedFieldKeys || [];
    const agentRestricted = restrictedFieldKeys.includes('agent_pii');

    if (agentRestricted) {
        return (
            <div>
                <div className="mb-3">
                    <label className="form-label">{i18n.t('jt_agent_first.text')}</label>
                    <RestrictedFieldInput />
                </div>
                <div className="mb-3">
                    <label className="form-label">{i18n.t('jt_agent_last.text')}</label>
                    <RestrictedFieldInput />
                </div>
                <div className="mb-3">
                    <label className="form-label">{i18n.t('jt_agent_external.text')}</label>
                    <RestrictedFieldInput />
                </div>
            </div>
        );
    }

    const initialValues: AgentFormValues = {
        firstName: '',
        lastName: '',
        externalId: '',
    };

    const customErrorHandling = (error: any, formik: any) => {
        if (error.response?.status === 400) {
            formik.setFieldError('externalId', error.response?.data?.message ?? i18n.t('invalid_data.text'));
            return true;
        }
        return false;
    };

    const fields: FormFieldConfig[] = [
        {
            name: 'firstName',
            label: i18n.t('jt_agent_first.text'),
            type: 'text',
            required: true,
            validation: yup.string()
                .max(100, i18n.t('jt_agent_first_name_max.text'))
                .required(i18n.t('jt_agent_first_name_required.text')),
        },
        {
            name: 'lastName',
            label: i18n.t('jt_agent_last.text'),
            type: 'text',
            required: true,
            validation: yup.string()
                .max(100, i18n.t('jt_agent_last_name_max.text'))
                .required(i18n.t('jt_agent_last_name_required.text')),
        },
        {
            name: 'externalId',
            label: i18n.t('jt_agent_external.text'),
            type: 'text',
            required: true,
            disabled: !!agentId,
            validation: yup.string()
                .max(100, i18n.t('jt_agent_external_id_max.text'))
                .required(i18n.t('jt_agent_external_id_required.text')),
        },
    ];

    return (
        <QuickSettingsForm
            entityId={agentId || undefined}
            crudConfig={{
                resourceUrl: window.URLS.agentsResource,
                storeActionPath: 'agents.addOrEdit',
                successMessageKey: 'jt_agent_successfully_action.text',
                entityName: 'agent'
            }}
            fields={fields}
            initialValues={initialValues}
            onSave={onSave}
            customErrorHandling={customErrorHandling}
            additionalStoreActions={[
                {
                    path: 'activeStoryObjectDetails.setReload',
                    value: true,
                },
                {
                    path: 'agents.setReload',
                    value: true,
                },
            ]}
        />
    );
});


export default AgentSettingsForm; 